<template>
    <form
        class="bg-white w-full md:rounded-lg"
        @submit.prevent.stop="onSubmit"
    >
        <div class="py-6 max-md:container md:pt-5 md:px-6">
            <div class="mb-5">
                <span class="text-lg font-medium">{{ $t('editPost') }}</span>
                <span
                    v-if="editPopupIsOpen"
                    class="icon icon-close h-5 w-5 cursor-pointer float-right text-slate-400 hover:text-slate-600 hidden md:inline-block"
                    @click="closeEditPopup"
                ></span>
            </div>
            <div class="inline-flex w-full relative">
                <UserAvatar
                    :name="isNewIdentityUser ? user.username : avatar.name"
                    :animated-avatar="post.postAnonymous ? null : user.gamify_avatar_url"
                    :picture="isNewIdentityUser ? userPicture : avatar.picture"
                    :is-anonymous="isNewIdentityAnonymous"
                    :show-details="false"
                    :show-admin-badge="user.hasAdminBadge"
                    class="mr-4 h-10 w-10 avatar-padding"
                ></UserAvatar>
                <div class="w-full">
                    <textarea
                        ref="text"
                        v-model="post.text"
                        v-dynamic-height
                        :class="[
                            {
                                'md:pr-26': !editPopupIsOpen,
                                'md:error': validation.errors.text,
                            },
                            'custom-scrollbar max-h-66 min-h-[2.5rem]',
                        ]"
                        :readonly="!editPopupIsOpen"
                        :placeholder="$t('Ask a question...')"
                        type="text"
                        class="py-1 px-2 rounded"
                        @click="openEditPopup"
                    />
                    <transition name="fade">
                        <p
                            v-if="validation.errors.text"
                            class="px-3 text-red-600 text-sm hidden md:block"
                            v-text="$t(validation.errors.text)"
                        ></p>
                    </transition>
                </div>

                <div
                    v-if="!editPopupIsOpen"
                    class="absolute py-2 right-0 leading-4 items-center hidden md:flex"
                >
                    <Btn
                        data-testid="post-edit-form_btn-image"
                        variant="tonal"
                        class="mr-2 text-slate-400"
                        content-color="slate-600"
                        icon="camera"
                        size="xxs"
                        color-on-touch
                        @click.prevent.stop="openPhoto"
                    />
                </div>
            </div>
            <div
                v-if="editPopupIsOpen"
                class="hidden md:inline-flex w-full h-10 mt-5 pl-14 justify-between"
            >
                <Btn
                    v-if="isAnonymityEnabled"
                    data-testid="post-edit-form_btn-popup-anonymous"
                    variant="tonal"
                    class="leading-10 !font-normal"
                    active-color="blue-600"
                    active-content-color="blue-600"
                    icon="incognito"
                    size="xs"
                    :label="$t('Anonym')"
                    :active="post.postAnonymous"
                    @click.prevent.stop="toggle('postAnonymous')"
                />
                <Btn
                    v-if="!post.hasPoll"
                    data-testid="post-edit-form_btn-popup-image"
                    variant="tonal"
                    class="leading-10 !font-normal"
                    active-color="blue-600"
                    active-content-color="blue-600"
                    icon="camera"
                    size="xs"
                    :label="$t('Photo')"
                    :active="post.hasPhoto"
                    @click.prevent.stop="toggle('hasPhoto')"
                />
                <BtnCta
                    data-testid="post-edit-form_btn-send"
                    icon="send"
                    class="ml-auto"
                    size="sm"
                    type="submit"
                    :disabled="isUpdating"
                />
            </div>
        </div>
        <transition-group
            v-if="editPopupIsOpen"
            class="hidden md:block"
            name="slide-open-y"
        >
            <div
                v-if="post.hasPhoto"
                style="max-height: 130px"
            >
                <!-- eslint-disable vue/no-bare-strings-in-template -->
                <PostPhotoUpload
                    :files="photo.files"
                    class="py-4 px-6 border-t"
                    spacer-left-width="3.5rem"
                    title="photo"
                    @delete="deletePhoto"
                    @upload-start="isUploading = true"
                    @upload-end="isUploading = false"
                ></PostPhotoUpload>
                <!-- eslint-enable vue/no-bare-strings-in-template -->
            </div>
        </transition-group>
    </form>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
import { BtnSize } from '@components/Btn';
import PostPhotoUpload from '@components/PostItem/PostPhotoUpload.vue';
import { featureIsEnabled } from '@/modules/features';
import { showSubscriptionShop } from '@helpers/subscription';

export default {
    name: 'PostEditForm',

    components: {
        PostPhotoUpload,
    },

    inject: [
        'avatar',
        'context',
        'post',
        'photo',
        'validation',
    ],
    emits: ['close-edit'],

    i18nOptions: {
        namespaces: ['common', 'post'],
    },

    data() {
        return {
            BtnSize,
            isAnonymous: this.post.postAnonymous,
            isUpdating: false,
            isUploading: false,
            isIdentityChanged: false,
        };
    },

    computed: {
        ...mapGetters('auth', ['userPicture', 'user']),
        ...mapGetters('ui', ['module']),
        isAnonymityEnabled() {
            return featureIsEnabled('anonymityIdentity') && this.user?.is_premium_system_enabled;
        },
        postId() {
            return this.post.questionId;
        },
        editPopupIsOpen() {
            return this.$store.getters['post/editPopupIsOpen'](this.postId);
        },
        isNewIdentityAnonymous() {
            return !this.isAnonymous && this.isIdentityChanged;
        },
        isNewIdentityUser() {
            return this.isAnonymous && this.isIdentityChanged;
        },
    },

    methods: {
        openEditPopup() {
            this.$store.dispatch('post/openEditPopup', this.postId);
        },
        closeEditPopup() {
            this.$store.dispatch('post/closeEditPopup', this.postId);
            this.$emit('close-edit');
        },
        onSubmit() {
            if (this.isIdentityChanged) {
                this.confirmIdentityChange();
                return;
            }
            this.update();
        },
        async confirmIdentityChange() {
            this.closeEditPopup();

            const result = await this.$modal.open(
                defineAsyncComponent(() => import('@/components/ModalConfirmPostIdentityChange')),
                {
                    isAnonymous: this.isAnonymous,
                    profileImage: this.userPicture,
                    gamifyAvatar: this.user.gamify_avatar_url,
                },
            );

            switch (result) {
                case true:
                    return this.update();
                case false:
                    return this.toggle('postAnonymous');
                default:
                    return null;
            }
        },
        async update() {
            if (this.isUpdating || this.isUploading || !this.validate()) {
                return;
            }
            this.isUpdating = true;
            let post = { ...this.post };
            const files = this.photo.files.filter((file) => file.id === undefined);
            if (this.post.hasPhoto) {
                post = {
                    ...post,
                    ...(files.length && { files }),
                    ...(this.photo.deleteFiles.length && {
                        deleteFiles: this.photo.deleteFiles,
                    }),
                };
            }
            delete post.hasPhoto;

            const { isIdentityChanged } = this;

            await this.$store
                .dispatch(`${this.module}/editPost`, {
                    context: this.context.type,
                    post,
                })
                .then((response) => this.onEdited(response, isIdentityChanged))
                .catch(this.onError)
                .finally(() => {
                    this.isUpdating = false;
                });
        },
        onEdited(response, isIdentityChanged) {
            if (isIdentityChanged) {
                this.$store.dispatch(`${this.module}/propagateIdentityChange`, {
                    postId: response.post.postDetails.id,
                    user: response.post.postDetails.user || response.post.postDetails.anonymousUser,
                    isAnonymous: response.post.postDetails.isAnonymous,
                });
            }
            this.closeEditPopup();
        },
        onError(error) {
            const key = new Date().getTime();
            const content = error.response?.data?.message || this.$t('errors.requestError');
            this.addError('text', content, key);
            this.$store.dispatch('flashMessages/showError', { content, key });
        },
        validate() {
            this.clearValidationError();
            if (this.post.text === '') {
                this.addError('text', 'Question Text is required');
            }
            Object.keys(this.validation.errors).forEach((field) => {
                const error = this.validation.errors[field];
                this.$store.dispatch('flashMessages/showError', {
                    content: error.message,
                    key: error.key,
                });
            });
            return this.validation.hasErrors === false;
        },
        clearValidationError() {
            Object.keys(this.validation.errors).forEach((field) => {
                const error = this.validation.errors[field];
                this.$store.dispatch('flashMessages/dismiss', error.key);
            });
            this.clearValidation();
        },
        openPhoto() {
            this.post.hasPhoto = true;
            this.openEditPopup();
        },
        toggle(type) {
            if (type === 'postAnonymous' && this.user?.is_premium_system_enabled && !this.user?.is_premium) {
                showSubscriptionShop('anonymous_posting_button_click');
                return;
            }

            if (type === 'postAnonymous') {
                this.isIdentityChanged = !this.isIdentityChanged;
            }

            this.post[type] = !this.post[type];
        },
        resizeTextarea() {
            if (this.$refs.text) {
                this.$refs.text.dispatchEvent(new CustomEvent('input'));
            }
        },
        async reset() {
            this.post.text = '';
            this.post.postAnonymous = false;
            this.post.hasPhoto = false;
            this.photo.files = [];
            this.photo.deleteFiles = [];
        },
        clearValidation() {
            this.validation.errors = {};
            this.validation.hasErrors = false;
        },
        errorItem(message, key) {
            return {
                message,
                key,
                toString() {
                    return this.message;
                },
            };
        },
        addError(field, message, key = null) {
            const error = [];
            error[field] = this.errorItem(message, key || new Date().getTime());
            this.validation.errors = { ...this.validation.errors, ...error };
            this.validation.hasErrors = true;
        },
        removeError(field) {
            delete this.validation.errors[field];
            if (Object.keys(this.validation.errors).length === 0) {
                this.validation.hasErrors = false;
            }
        },
        deletePhoto(file) {
            this.photo.deleteFiles.push(file);
        },
    },
};
</script>

<style lang="scss" scoped>
textarea {
    @apply outline-none resize-none;
}
</style>
